import React from "react";
import { Link } from "react-router-dom";
import { Skeleton, SkeletonText } from "../Skeleton";

interface WidgetFrameProps {
  title: string;
  children: React.ReactNode;
  /** Sits beside the title as quiet context — "4 groups · 18 assets" in the design. */
  meta?: string;
  /** Right sends the meta to the header's far end, into the slot the action link would otherwise hold. */
  metaAlign?: "left" | "right";
  count?: number;
  actionLabel?: string;
  actionHref?: string;
  /** Use instead of actionHref where the action opens a modal rather than navigating. */
  onAction?: () => void;
  /** A control in the action slot instead of a link — a picker the widget is steered by. */
  action?: React.ReactNode;
  isLoading?: boolean;
  /** Off for widgets that lay out their own rows edge-to-edge (lists, the map). */
  padBody?: boolean;
  /** On for a frame given a fixed height, so overflowing rows scroll instead of escaping the card. */
  scrollBody?: boolean;
  className?: string;
}

// Link or button, the action reads identically — the frame's only accent
const ACTION_CLASS =
  "shrink-0 font-outfit text-eai-body-xs font-medium text-eai-cta-primary transition-colors hover:text-eai-cta-primary-hover";

// One structure for every widget: ruled header (title, count, one accent action) then body — depth from the rule, not shadows
const WidgetFrame: React.FC<WidgetFrameProps> = ({
  title,
  children,
  meta,
  metaAlign = "left",
  count,
  actionLabel,
  actionHref,
  onAction,
  action,
  isLoading = false,
  padBody = true,
  scrollBody = false,
  className = "",
}) => (
  <section
    className={`flex min-w-0 flex-col overflow-hidden rounded-eai-card border border-eai-border bg-eai-surface-card shadow-eai-card ${className}`}
  >
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-eai-border px-5 py-3.5">
      <div className="flex min-w-0 items-center gap-2.5">
        {/* shrink-0: the title is the widget's identity, so the meta beside it absorbs the squeeze */}
        <h2 className="eai-card-title shrink-0 truncate">
          {title}
        </h2>
        {typeof count === "number" && count > 0 && (
          <span className="eai-count-pill shrink-0">
            {count}
          </span>
        )}
        {/* Quiet context, so it goes first on a narrow card — at 327px it truncated the title it qualifies to a letter */}
        {meta && metaAlign === "left" && (
          <span className="hidden min-w-0 truncate font-inter text-eai-body-xs text-eai-ink-faint md:inline">
            {meta}
          </span>
        )}
      </div>

      {meta && metaAlign === "right" && (
        <span className="hidden min-w-0 max-w-[55%] truncate font-inter text-eai-body-xs text-eai-ink-faint md:inline">
          {meta}
        </span>
      )}
      {/* The one accent in the frame — the action is where the eye should land after the title */}
      {action}
      {!action && actionLabel && actionHref && (
        <Link to={actionHref} className={ACTION_CLASS}>
          {actionLabel}
        </Link>
      )}
      {!action && actionLabel && !actionHref && onAction && (
        <button type="button" onClick={onAction} className={ACTION_CLASS}>
          {actionLabel}
        </button>
      )}
    </div>

    <div
      className={`min-h-0 flex-1 ${padBody ? "px-5 py-4" : "py-1.5"} ${
        scrollBody ? "overflow-y-auto" : ""
      }`}
    >
      {isLoading ? (
        <div className={`flex flex-col gap-3 ${padBody ? "" : "px-5 py-2.5"}`}>
          <SkeletonText lines={2} />
          <Skeleton className="h-3.5 w-1/2" />
        </div>
      ) : (
        children
      )}
    </div>
  </section>
);

export default WidgetFrame;
